import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input, SimpleChanges, output } from '@angular/core';
import { finalize, lastValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PosSalesService } from 'src/app/services/possalesservice';
import { LoaderComponent } from '../loader/loader.component';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from '../shared-imports';
import { GeneralService } from 'src/app/services/general.service';
import Swal from 'sweetalert2';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { SharedHTMLSettingsDataService } from '../sharedHTMLSettingsData.service';
import { MarketInfoService } from 'src/app/services/marketInfo.service';
import { CommonCalculatorService } from '../commonCalculators.service';
import { ECountrycode } from '../enum';

@Component({
  selector: 'app-pos-customer-search',
  templateUrl: './pos-customer-search.component.html',
  standalone: true,
  imports: [ANGULAR_MODULES, LoaderComponent, SYNCFUSION_MODULES],
  styleUrls: ['./pos-customer-search.component.scss']
})
export class POSCustomerSearchComponent implements OnInit, OnDestroy {
  public loaderIcon: Promise<boolean>;
  public data: any[] = [];
  private subscriptions: Subscription[] = [];

  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  @Output() customerData = new EventEmitter<any>();
  @Output() closeCustomerInfoModal = new EventEmitter<any>();
  @Input() customerSearch;
  @Input() newForm;
  @Input() customerId;
  public PhoneNumberMask: String = '(000)000-0000';
  photoPreview: string | ArrayBuffer | null = null;
  selectedFile: File | null = null;

  pageNumber = 0;
  pageSize = 20;
  allRecords: any[] = [];
  searchField = "MarketCustomerId";
  searchValue;
  states = [];
  countries = [];
  customerForm: FormGroup;
  private destroy$ = new Subject<void>();
   get showStateDropDown(): boolean {
    return this.states.length > 0;
  }
  imagePreviewUrl: string = './assets/images/NoPhoto.svg';
  customerPhoto;
  imageAddbtnText = "ADD";
  // guards against duplicate customer records when Save is clicked repeatedly
  isSavingCustomer = false;

  private phoneMaskValidator(control: AbstractControl): ValidationErrors | null {
    const rawValue = (control.value ?? '').toString();
    const digits = rawValue.replace(/\D/g, '');

    // Optional field: only validate when user enters a value.
    if (!rawValue || digits.length === 0) {
      return null;
    }

    return digits.length === 10 ? null : { invalidPhone: true };
  }

  constructor(private posSalesService: PosSalesService,
    private toastr: ToastrService,
    private generalService: GeneralService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private formbuilder: FormBuilder,
    private marketInfoService: MarketInfoService,
    private commonCalculatorService: CommonCalculatorService
  ) {
    this.customerForm = this.formbuilder.group({
      customerId: [{ value: '', disabled: true }],
      marketCustomerId: [{ value: '', disabled: true }],
      taxExemptionNumber: [],
      inactiveCustomer: [false],
      firstName: ['', [Validators.required, this.noWhitespaceValidator]],
      lastName: [''],
      companyName: [''],
      address: [''],
      email: ['', Validators.email],
      driverLicenseNo: [''],
      city: [''],
      stateId: [''],
      countryId: [''],
      zip: [''],
      driverLicenseExpiryDate: [new Date()],
      telephone: ['', [this.phoneMaskValidator.bind(this)]],
      cellPhone: ['', [this.phoneMaskValidator.bind(this)]],
      customerNotes: [''],
      stateCode: ['']
    })
  }

  ngOnInit(): void {
    console.log("customerSearch" + this.customerSearch);
    this.loadAllCustomers();
    this.loadCountries();
    this.loadStates(1);
  }

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  sort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.data.sort((a, b) => {
      let valueA = a[column] ?? '';
      let valueB = b[column] ?? '';

      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log("ngOnChanges triggered", changes);

    if (this.newForm) {
      this.customerForm.reset();
      this.imagePreviewUrl  = './assets/images/NoPhoto.svg';
    }
    if (!this.customerSearch && !this.newForm && this.customerId) {
      this.getCustomerInfo(this.customerId);
    }
  }

  validateForm() {
    if (this.customerForm.valid)
      return false;
    return true;
  }

  // Validators.required accepts whitespace-only text; treat it as blank
  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value === 'string' && value.length > 0 && value.trim().length === 0) {
      return { required: true };
    }
    return null;
  }

  loadCountries(): void {
    this.generalService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.length > 0) {
            this.countries = res;
          } else {
            // Default to US if empty
            this.countries = [{ Code: 1, Name: 'US' }];
          }
        },
        error: (err) => {
          console.error('Failed to load countries', err);
          this.countries = [{ Code: 1, Name: 'US' }];
        }
      });
  }

  loadStates(countryId): void {
    this.generalService.getStates(countryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.states = res;
        },
        error: (err) => {
          console.error('Failed to load US States', err);
        }
      });
  }

  async onCountryChange() {
    this.states = [];
    const countryId = Number(this.customerForm.get('countryId').value);
    this.states = await lastValueFrom(this.generalService.getStates(countryId));
    if (this.states.length == 0) {
      this.customerForm.get('stateId').clearValidators();
      this.customerForm.get('stateCode').setValidators([Validators.required, this.noWhitespaceValidator]);
    }
    else {
      this.customerForm.get('stateId').setValidators(Validators.required);
      this.customerForm.get('stateCode').clearValidators();
    }
    this.commonCalculatorService.setZipCodeValidator(countryId, this.customerForm.get('zip'));
    this.customerForm.get('zip').removeValidators(Validators.required);
    this.customerForm.get('zip').updateValueAndValidity();
    this.customerForm.get('stateId').updateValueAndValidity();
    this.customerForm.get('stateCode').updateValueAndValidity();
  }

  loadAllCustomers(): void {
    this.loaderIcon = Promise.resolve(true);
    const vendorFilter = {
      PageNumber: this.pageNumber,
      PageSize: this.pageSize,
      SearchField: this.searchField,
      SearchValue: this.searchValue
    }
    this.subscriptions.push(
      this.posSalesService.getPOSCustomerList(vendorFilter)
        .subscribe((res: any) => {
          if (res && res.length > 0) {
            const mapped = res.map((el) => {
              return {
                CustomerId: el.CustomerId,
                MarketCustomerId: el.MarketCustomerId,
                FirstName: el.FirstName,
                LastName: el.LastName,
                Address: el.Address,
                City: el.City,
                StateId: el.StateId,
                CountryId: el.CountryId,
                Zip: el.Zip,
                Telephone: el.Telephone,
                CellPhone: el.CellPhone,
                Email: el.Email,
                TaxExemptionNumber: el.TaxExemptionNumber,
                CustomerNotes: el.CustomerNotes,
                DriverLicenseNo: el.DriverLicenseNo,
                DriverLicenseExpiryDate: el.DriverLicenseExpiryDate,
                InactiveCustomer: el.InactiveCustomer
              };
            });

            this.data = [...this.data, ...mapped];

            this.pageNumber++;
          }
          this.loaderIcon = Promise.resolve(false);
        }, error => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error('Error loading vendors');
        })
    );
  }

  onScroll(): void {
    const el = this.scrollContainer.nativeElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      this.loadAllCustomers(); // directly call API for next page
    }
  }

  resetAndLoad(): void {
    this.pageNumber = 0;
    this.data = [];
    this.imagePreviewUrl = "";
    this.loadAllCustomers();
  }

  CustomerDetails(item: any, event: MouseEvent): void {
    event.stopPropagation();
    this.customerData.emit(item);
    this.closeCustomerModal();
  }

  addImageClick() {
    const el = document.getElementById('ImageFileInput') as HTMLElement;
    if (el) {
      el.click();
    }
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  addImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG and PNG files are allowed.');
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/jpeg') {
          // Already JPG → no conversion needed
          const base64Content = (reader.result as string).split(',')[1];
          this.imagePreviewUrl = reader.result as string;
          this.customerPhoto = {
            ...this.customerPhoto,
            UploadedcustomerPhotoContent: base64Content,
            customerPhotoName: file.name, // keep original name
            DeleteFlag: false,
          };
          input.value = '';
        } else {
          // Convert PNG → JPG
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9); // JPG output
            const base64Content = jpgDataUrl.split(',')[1];
            this.imagePreviewUrl = jpgDataUrl;
            this.customerPhoto = {
              ...this.customerPhoto,
              UploadedcustomerPhotoContent: base64Content,
              customerPhotoName: file.name.replace(/\.[^/.]+$/, '') + '.jpg', // force .jpg extension
              DeleteFlag: false,
            };
            input.value = '';
          };
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  get isDefaultImage(): boolean {
    return this.imagePreviewUrl === './assets/images/NoPhoto.svg';
  }

  get canRemoveImage(): boolean {
    if (!this.isDefaultImage && !this.customerPhoto.DeleteFlag) {
      this.imageAddbtnText = "UPDATE"
      return true;
    } else {
      this.imageAddbtnText = "ADD"
      return false;
    }
  }

  removeImage() {
    this.customerPhoto = {
      customerPhotoName: '',
      UploadedcustomerPhotoContent: null,
      DeleteFlag: true
    };
    this.imagePreviewUrl = './assets/images/NoPhoto.svg';
    const fileInput = document.getElementById('ImageFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  getCustomerInfo(customerId) {
    this.posSalesService.getCustomerDetailsById(customerId)
      .pipe().subscribe(async details => {
        this.customerData.emit(details);
        const timestamp = new Date().getTime();
        if (details) {
          this.imagePreviewUrl = details?.CustomerPhoto ? details.CustomerPhoto + `?timestamp=${timestamp}` : "./assets/images/NoPhoto.svg";
          if (details?.CustomerPhoto) {
            this.customerPhoto = {
              DeleteFlag: false
            }
          }
          if (this.countries.length === 0) {
            this.countries = await lastValueFrom(this.generalService.getCountries());
          }
          var countryId = this.countries.find(c => c.Name === details.CountryId)?.Code;
          this.customerForm.patchValue({
            customerId: details.CustomerId,
            marketCustomerId: details.MarketCustomerId,
            taxExemptionNumber: details.TaxExemptionNumber,
            inactiveCustomer: details.InactiveCustomer,
            firstName: details.FirstName,
            lastName: details.LastName,
            companyName: details.CompanyName,
            address: details.Address,
            email: details.Email,
            driverLicenseNo: details.DriverLicenseNo,
            city: details.City,
            zip: details.Zip,
            driverLicenseExpiryDate: details.DriverLicenseExpiryDate,
            telephone: details.Telephone,
            cellPhone: details.CellPhone,
            customerNotes: details.CustomerNotes,
            countryId: countryId,
          });
          await this.onCountryChange();
          this.customerForm.patchValue({
            stateId: details.StateId,
            stateCode: details.StateId
          });
        }
      })
  }

  saveCustomerDetails() {
    if (this.isSavingCustomer) return;
    var data = this.customerForm.value;
    Object.keys(data).forEach(key => {
      if (typeof data[key] === 'string') data[key] = data[key].trim();
    });
    if (this.customerForm.valid) {
      data.customerId = this.customerForm.get('customerId').value || 0;
      data.countryId = this.countries.find(e=>e.Code == data.countryId)?.Name;
      data.stateId = this.showStateDropDown ? data.stateId : data.stateCode;
      if (this.customerPhoto) {
        var payload = {
          ImageName: this.customerPhoto.customerPhotoName || '',
          UploadedImageContent: this.customerPhoto.UploadedcustomerPhotoContent || null,
          DeleteFlag: this.customerPhoto.DeleteFlag || false,
        };
      }
      data.ImageModel = payload;
      this.isSavingCustomer = true;
      this.posSalesService.savePOSCustomerDetails(data)
        .pipe(finalize(() => this.isSavingCustomer = false))
        .subscribe(res => {
          if (!res?.Success) {
            // backend returns Success=false (Id 0) when the save fails, e.g. a value exceeds the column length
            this.toastr.error($localize`Unable to save customer details. Please check the entered values and try again.`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            return;
          }
          if (data.customerId) {
            this.toastr.success($localize`Information updated successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
          }
          else { this.toastr.success($localize`Saved with Customer Id ${res.MarketDataId}`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig); }
          this.customerForm.reset();
          var custData = {
            CustomerId: res.Id,
          }
          this.customerData.emit(custData);
          this.resetAndLoad();
          this.closeCustomerInfoModal.emit();
          this.getCustomerInfo(res.Id);
        });
    } else {
      Swal.fire({
        title: $localize`Warning`,
        text: $localize`Please provide the neccessary info.`,
        icon: "warning",
        confirmButtonText: $localize`Ok`,
      });
    }
  }

  closeCustomerModal() {
    this.customerForm.reset();
    this.resetAndLoad();
    this.closeCustomerInfoModal.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s?.unsubscribe());
  }
}
